
export interface ArchiveFile {
  id: string;
  name: string;
  type: 'html' | 'directory';
  path: string;
  category?: string;
  summary?: string;
  lastModified?: string;
  size?: string;
  imageUrl?: string;
  description?: string;
  author?: string;
  status?: 'published' | 'pending';
  content?: string;
  startYear?: number;
  endYear?: number;
}

export interface SearchResult {
  file: ArchiveFile;
  relevanceScore: number; // 0-100
  snippet: string;
  displayUrl: string;
  aiInsights: string;
}

export interface ChatMessage {
  role: 'user' | 'ai';
  text: string;
}

export interface DiscussionThread {
  id: string;
  query: string;
  title: string;
  history: ChatMessage[];
  lastUpdated: number;
}

export interface AIOverview {
  history: ChatMessage[];
  narrowingQuestion: string;
  options: string[];
  citations: {
    title: string;
    path: string;
    index: number;
  }[];
}

export enum Category {
  HEALTH = 'Health & Healing',
  SPIRITUALITY = 'Spirituality & Meditation',
  DREAMS = 'Dream Interpretation',
  HISTORY = 'Ancient Civilisations',
  PHILOSOPHY = 'Philosophy & Reincarnation',
  RECORDS = 'Akashic Records',
}

// Extended DB Row for IndexedDB persistence
export interface DBRow extends ArchiveFile {
    content: string; // Plain text content
    indexed: boolean;
    cachedAt?: number;
}

// --- Community Page Types ---

export interface User {
  uid: string; // Added uid
  name: string;
  email: string;
  picture: string;
  monthlySpendCap?: number;
  topupBalance?: number;
  currentUsage?: number;
  hasPaymentMethod?: boolean;
  primaryPaymentMethod?: 'stripe' | 'google-pay' | null;
  customStripeSecretKey?: string;
  customStripePublishableKey?: string;
  notifications?: {
    pushEnabled: boolean;
    emailEnabled: boolean;
    newArticle: boolean;
    newFeedPost: boolean;
    newFeedPostInGroup: boolean;
    commentsOnMyPost: boolean;
    responsesToMyComment: boolean;
  };
}

export interface Bookmark {
  id: string;
  title: string;
  fileId: string;
  fileName: string;
  paragraphId?: string; // Optional for direct highlight links
  paragraphText?: string;
  categoryPath: string; // e.g., "Health/Healing"
  timestamp: number;
  note?: string;
}

export interface BookmarkCategory {
  id: string;
  name: string;
  parentId: string | null;
  path: string; // Full path for easy filtering
}

export interface Comment {
  id: string;
  author: {
    uid: string;
    name: string;
    avatar: string;
  };
  content: string;
  timestamp: string | number;
}

export interface Group {
  id: string;
  name: string;
  description: string;
  memberCount: number;
  isMember: boolean;
  image: string;
  notifications?: boolean; // Added for settings
}

export interface Post {
  id: string;
  author: {
    uid: string;
    name: string;
    avatar: string;
  };
  timestamp: string | number;
  content: string;
  type: 'text' | 'image' | 'link';
  mediaUrl?: string;
  likes: number;
  likedByMe: boolean;
  comments: Comment[];
  category: 'Update' | 'Question' | 'Insight' | 'Media';
  groupId?: string;
}
